import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { ExporterFileType } from 'dashboard/models/exporter';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/transactions';
import { useListTransactionsQuery } from 'dashboard/redux/api/transaction';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { RootState } from 'dashboard/redux/store';
import { loadLists } from 'dashboard/redux/thunk-actions/list';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import { createExporterTransaction } from 'dashboard/redux/thunk-actions/transaction';
import { loadTransactionStatuses } from 'dashboard/redux/thunk-actions/transaction-statuses';
import { loadTransactionTypesFilterOptions } from 'dashboard/redux/thunk-actions/transaction-types';
import DashboardTransactionsView from 'dashboard/views/DashboardTransactionsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { updateDateFilters } from 'lib/utils/filters';

const DashboardTransactionsRoute = (props: PropsFromRedux) => {
  const {
    currentEnvironment,
    currentPage,
    pageSize,
    transactionsFilters,
    transactionsSortParams,
    organization,
  } = props;
  const productFeatureIsDisabled = productFeatureDisabled(
    organization,
    ProductFeatureName.TransactionsDashboard,
    currentEnvironment?.type,
  );

  const {
    handlePageLinkClick,
    handlePageSizeClick,
    handleOmniSearch,
    handleSortUpdate,
    handleDateUpdate,
  } = useTable({
    filters: transactionsFilters,
    sortParams: transactionsSortParams,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: props.setFilters,
    setSortParams: props.setSortParams,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
  });

  const {
    isLoading: isLoadingTransactionsList,
    data: { data: transactions = [], meta: pageMeta } = {},
    error: transactionsListError,
    isFetching: isFetchingTransactionsList,
  } = useListTransactionsQuery({
    filter: {
      'environment-id': currentEnvironment.id,
      'created-at-start': transactionsFilters && transactionsFilters.createdAtStart?.toString(),
      'created-at-end': transactionsFilters && transactionsFilters.createdAtEnd?.toString(),
      'tag-ids': transactionsFilters && transactionsFilters.tagIds,
      'transaction-type-ids': transactionsFilters && transactionsFilters.transactionTypeIds,
      'transaction-status-ids': transactionsFilters && transactionsFilters.transactionStatusIds,
      omni: transactionsFilters && transactionsFilters.omni,
      'list-ids': transactionsFilters && transactionsFilters.listIds,
    },
    sort: `${transactionsSortParams?.shouldSortDescending ? '-' : ''}${transactionsSortParams?.sort}`,
    page: { number: currentPage, size: pageSize },
  });

  useRTKErrorHandler({
    error: transactionsListError,
    onError: () => ({
      message: 'Failed to load Transactions.',
      level: MessageType.Failure,
    }),
  });

  const {
    isLoading: isLoadingTransactionTypesList,
    data: { data: transactionTypes = [] } = {},
    error: transactionTypesError,
  } = useListTransactionTypesQuery({ filter: {} });

  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({ message: 'Failed to load Transaction Types.', level: MessageType.Failure }),
  });

  const { loading: loadTransactionTypesFilterOptions } = useApiLoader({
    loader: async () => await props.loadTransactionTypesFilterOptions({ filters: {} }),
    deps: [props.loadTransactionTypesFilterOptions],
    skip: productFeatureIsDisabled,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError: null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onNetworkError: null,
  });

  const { loading: isLoadingTransactionStatuses } = useApiLoader({
    loader: () => props.loadTransactionStatuses(),
    deps: [props.loadTransactionStatuses],
    skip: productFeatureIsDisabled,
    onError: (error) => {
      /*
       * Ignore permissions errors right now since we already check to see
       * if they can view transaction types.
       */
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load transaction statuses.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingTransactionTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Transaction),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: loadingTransactionExportCreate,
    error: exportError,
    callback: handleTransactionsExportCreate,
  } = useApiCallback(
    async (fileType: ExporterFileType) => {
      await props.createExporterTransaction({
        currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: transactionsFilters,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: transactionsSortParams,
        fileType: fileType,
      });
      props.showMessage('You will receive an email when your export is ready.', MessageType.Notice);
    },
    [
      props.createExporterTransaction,
      currentEnvironment,
      transactionsFilters,
      transactionsSortParams,
    ],
  );

  useApiErrorHandler({
    error: exportError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create export.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () =>
      props.loadLists({
        currentEnvironment,
        filters: {},
        sortParams: { sort: '-', shouldSortDescending: false },
      }),
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load lists.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      updateDateFilters(newDateRange, handleDateUpdate);
    },
    [handleDateUpdate],
  );

  const handleTagFilterUpdate = (tagIds: string[]) => {
    props.setFilters({
      tagIds: tagIds,
    });
    props.setCurrentPage(1);
  };

  const handleTransactionTypeFilterUpdate = (transactionTypeIds: string[]) => {
    props.setFilters({
      transactionTypeIds,
    });
    props.setCurrentPage(1);
  };

  const handleTransactionStatusFilterUpdate = (transactionStatusIds: string[]) => {
    props.setFilters({
      transactionStatusIds,
    });

    props.setCurrentPage(1);
  };

  const handleListFilterUpdate = (listIds: string[]) => {
    props.setFilters({ listIds });
    props.setCurrentPage(1);
  };

  return (
    <DashboardTransactionsView
      transactions={transactions}
      transactionStatuses={props.transactionStatuses}
      transactionTypes={transactionTypes}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      transactionPaginationParams={pageMeta}
      tags={props.tags}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      currentPage={currentPage}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      transactionsFilters={transactionsFilters}
      transactionTypeFilterOptions={props.transactionTypeFilterOptions}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={transactionsSortParams}
      isLoadingTransactions={
        isLoadingTransactionsList ||
        isFetchingTransactionsList ||
        isLoadingTransactionTypesList ||
        isLoadingTransactionStatuses ||
        isLoadingTransactionTags ||
        loadTransactionTypesFilterOptions
      }
      currentEnvironment={currentEnvironment}
      lists={props.lists}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handleSortUpdate={handleSortUpdate}
      handleTagFilterUpdate={handleTagFilterUpdate}
      handleTransactionTypeFilterUpdate={handleTransactionTypeFilterUpdate}
      handleTransactionStatusFilterUpdate={handleTransactionStatusFilterUpdate}
      handleTransactionsExportCreate={handleTransactionsExportCreate}
      handleListFilterUpdate={handleListFilterUpdate}
      productFeatureIsDisabled={productFeatureIsDisabled}
      isLoadingTransactionExportCreate={loadingTransactionExportCreate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    transactionsFilters: state.transactions.filters,
    transactionsSortParams: state.transactions.sortParams,
    transactionPaginationParams: state.transactions.paginationParams,
    pageSize: state.transactions.pageSize,
    currentPage: state.transactions.currentPage,
    environments: state.organization.environments,
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    tags: state.tags.tags.transaction,
    transactionStatuses: state.transactionStatuses.transactionStatuses,
    transactionTypeFilterOptions: state.transactionTypes.filterOptions,
    lists: state.lists.lists,
  };
};

const mapDispatchToProps = {
  createExporterTransaction,
  loadTransactionTypesFilterOptions,
  loadTags,
  loadTransactionStatuses,
  loadLists,
  setPageSize,
  setCurrentPage,
  showMessage,
  setFilters,
  setSortParams,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTransactionsRoute);
